import type { UserManager } from 'oidc-client-ts';
import { config } from '../../config';
import { routeStorage } from '../login/storage';
import { reportErrorToSentry } from './sentry';
import { trace } from './trace';

const saveCurrentRoute = () => {
    const initialRoute = `/${window.location.hash}`;

    routeStorage.saveRoute(initialRoute);

    trace('saving initial route', initialRoute);
};

const doSignIn = (userManager: UserManager, isAuthCodeRedirect: boolean) =>
    isAuthCodeRedirect ? userManager.signinCallback() : userManager.signinSilent();

const redirectIfStoredStateIsMissing = async (userManager: UserManager) => {
    const searchParams = new URLSearchParams(window.location.search);
    const state = searchParams.get('state');
    if (state !== null && (await userManager.settings.stateStore.get(state)) === null) {
        // Ensure that there is a state stored. In some cases, clearStaleState might remove the state from storage
        // which leads to issues when loading the page
        console.warn(`Signin error: No matching state found in storage. Redirecting to ${window.location.origin}`);

        routeStorage.discardRoute();

        // We want a real page reload, which will either:
        //  - perform a silent signin, which succeeds if the user already has a session from another login
        //  - trigger a new redirection that brings us back here with a valid state value.
        // We use replace here to override the redirect URL and prevent the back button to go
        // back to the redirect page.
        window.location.replace(window.location.origin);

        // The window.location.replace call runs in the background. We do not want
        // to continue with the rest of the authorization flow. This would just lead
        // to a thrown error from signinCallback(), which we would see in the monitoring.
        // Therefore, we block this promise to let the navigation happen.
        await new Promise<never>(() => {});
    }
};

export const attemptInitialSignIn = async (userManager: UserManager) => {
    const isAuthCodeRedirect =
        window.location.href.startsWith(config.login.redirectUri as string) &&
        /[?&]code=[^&]*/.test(window.location.search);

    await redirectIfStoredStateIsMissing(userManager);

    try {
        const user = await doSignIn(userManager, isAuthCodeRedirect);
        const initialRoute = routeStorage.getRoute();

        trace('initialRoute lookup', initialRoute);

        const redirectedForDeprecatedLocation = redirectForDeprecatedLocation(initialRoute);

        if (initialRoute && isAuthCodeRedirect && !redirectedForDeprecatedLocation) {
            trace(`Go to location "${initialRoute}"`);
            routeStorage.discardRoute();
            window.history.replaceState({}, '', initialRoute);
        } else if (isAuthCodeRedirect && !redirectedForDeprecatedLocation) {
            trace(`Go to location "${window.location.origin}"`);
            window.history.replaceState({}, '', window.location.origin);
        } else {
            routeStorage.discardRoute();
        }

        return user;
    } catch (error) {
        trace('oidc.signinSilent failed, trying page redirect...', error);
        const needToSigninError = new Error('Need to sign in');

        if (config.login.preventRedirect) {
            console.warn('[feature/login] redirect prevented due to config. Error was', error);
            return Promise.reject(needToSigninError);
        } else if (isAuthCodeRedirect) {
            trace('oidc.signinSilent.error', 'redirect prevented due to suspicious signin error', error);
            routeStorage.discardRoute();
            reportErrorToSentry(error);
            return Promise.reject(needToSigninError);
        } else {
            saveCurrentRoute();
            try {
                // Code after userManager.signinRedirect() will still be executed until
                // the current page is completely unloaded which happens asynchronously.
                // Hence, you should structure the code such that nothing relevant is executed afterward.
                await userManager.signinRedirect();
                return undefined;
            } catch (e) {
                console.error('SigninRedirect Failed when attemptInitialSignIn', e);
                return Promise.reject(new Error('SigninRedirect Failed when attemptInitialSignIn'));
            }
        }
    }
};

const redirectForDeprecatedLocation = (initialRoute: string | null) => {
    if (initialRoute && initialRoute.indexOf('/#assets') >= 0) {
        const newRoute = '/';
        trace(`Deprecated url loaded. Redirecting to "${newRoute}"`);
        window.history.replaceState({}, '', newRoute);
        return true;
    }
    return false;
};
